import { useMemo, useState } from "react";
import useTranslation from "../../../client/hooks/useTranslation";
import { useLiteData, type Charge } from "../../data/LiteDataProvider";
import { Card, Pnl, Segmented } from "../../ui/ui";
import { periodRows, roiOf, type Granularity, type PfmAccount } from "./pfmModel";
import { RoiText } from "./shared";

const GRANULARITY_KEY = "edgely:lite:pfm:periodGranularity";
const GRANULARITIES: Granularity[] = ["month", "quarter", "year"];

const th = "font-normal px-3 py-2";
const td = "px-3 py-3 text-right font-mono tabular-nums";

/** Suivi par période (mois / trimestre / année) : mêmes règles que le récap du mois, sur toute la vie du périmètre. */
export function PeriodTable({ accounts, charges }: { accounts: PfmAccount[]; charges: Charge[] }) {
  const t = useTranslation().lite.pfm.byPeriod;
  const { fmt } = useLiteData();
  // Préférence d'affichage de ce navigateur : le mois par défaut.
  const [granularity, setGranularityState] = useState<Granularity>(() => {
    try {
      const v = localStorage.getItem(GRANULARITY_KEY);
      return GRANULARITIES.includes(v as Granularity) ? (v as Granularity) : "month";
    } catch { return "month"; }
  });
  const setGranularity = (g: Granularity) => {
    setGranularityState(g);
    try { localStorage.setItem(GRANULARITY_KEY, g); } catch { /* stockage indisponible */ }
  };

  const rows = useMemo(() => periodRows(accounts, charges, granularity), [accounts, charges, granularity]);
  const total = useMemo(() => rows.reduce(
    (s, r) => ({
      payouts: s.payouts + r.b.payouts, payoutsCount: s.payoutsCount + r.b.payoutsCount,
      challenges: s.challenges + r.b.challenges, activations: s.activations + r.b.activations,
      charges: s.charges + r.b.recurring + r.b.oneOff, expenses: s.expenses + r.b.total, net: s.net + r.net,
    }),
    { payouts: 0, payoutsCount: 0, challenges: 0, activations: 0, charges: 0, expenses: 0, net: 0 },
  ), [rows]);

  const label = (start: Date) => {
    if (granularity === "year") return String(start.getFullYear());
    if (granularity === "quarter") return t.quarterLabel(Math.floor(start.getMonth() / 3) + 1, start.getFullYear());
    return fmt.monthYear(start);
  };
  const expense = (v: number) => (v > 0.005
    ? <span className="text-pnlNegative">{fmt.money(-v)}</span>
    : <span className="text-muted-foreground/70">-</span>);
  const payouts = (v: number, n: number) => (v > 0
    ? <span className="text-pnlPositive">{fmt.money(v)}{n > 0 && <span className="ml-1 text-[10px] text-muted-foreground/70">×{n}</span>}</span>
    : <span className="text-muted-foreground/70">-</span>);

  return (
    <Card
      title={t.title}
      subtitle={t.subtitle}
      pad={false}
      action={
        <Segmented
          size="sm"
          value={granularity}
          onChange={setGranularity}
          options={[{ id: "month", label: t.month }, { id: "quarter", label: t.quarter }, { id: "year", label: t.year }]}
        />
      }
    >
      {rows.length ? (
        <div className="overflow-x-auto mt-2">
          <table className="w-full text-sm min-w-[880px]">
            <thead>
              <tr className="text-[11px] text-muted-foreground/70 border-y border-border">
                <th className={`${th} text-left pl-5`}>{t.period}</th>
                <th className={`${th} text-right`}>{t.payouts}</th>
                <th className={`${th} text-right`}>{t.challenges}</th>
                <th className={`${th} text-right`}>{t.activations}</th>
                <th className={`${th} text-right`}>{t.charges}</th>
                <th className={`${th} text-right`}>{t.totalExpenses}</th>
                <th className={`${th} text-right`}>{t.net}</th>
                <th className={`${th} text-right pr-5`}>{t.roi}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map(r => {
                const quiet = !r.b.payouts && !r.b.total;
                return (
                  <tr key={r.key} className={quiet ? "text-muted-foreground/70" : ""}>
                    <td className="pl-5 px-3 py-3 font-medium whitespace-nowrap">{label(r.start)}</td>
                    <td className={td}>{payouts(r.b.payouts, r.b.payoutsCount)}</td>
                    <td className={td}>{expense(r.b.challenges)}</td>
                    <td className={td}>{expense(r.b.activations)}</td>
                    <td className={td}>{expense(r.b.recurring + r.b.oneOff)}</td>
                    <td className={td}>{expense(r.b.total)}</td>
                    <td className="px-3 py-3 text-right">{quiet ? <span className="font-mono tabular-nums">-</span> : <Pnl value={r.net} className="font-medium" />}</td>
                    <td className="pr-5 px-3 py-3 text-right"><RoiText roi={roiOf(r.b.payouts, r.b.total)} /></td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t border-border font-medium">
                <td className="pl-5 px-3 py-3">{t.total}</td>
                <td className={td}>{payouts(total.payouts, total.payoutsCount)}</td>
                <td className={td}>{expense(total.challenges)}</td>
                <td className={td}>{expense(total.activations)}</td>
                <td className={td}>{expense(total.charges)}</td>
                <td className={td}>{expense(total.expenses)}</td>
                <td className="px-3 py-3 text-right"><Pnl value={total.net} className="font-semibold" /></td>
                <td className="pr-5 px-3 py-3 text-right"><RoiText roi={roiOf(total.payouts, total.expenses)} /></td>
              </tr>
            </tfoot>
          </table>
        </div>
      ) : <p className="px-5 pb-5 pt-2 text-sm text-muted-foreground">{t.none}</p>}
    </Card>
  );
}
